import moment from "moment";
import { getSalesByLevel3, getSalesSummaryCards } from "@/api/financialReport";
import { resolveDateRange, buildMonthHeaders } from "./utils/dateRange";

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

const state = {
    /** Filtro de agrupación (Cuentas, Clientes, Tiendas, etc.) */
    activeSubFilter: "cuentas",
    /**
     * Cabeceras de columna calculadas tras el fetch.
     * Ejemplo: ["ene 2025", "feb 2025", ...]
     * @type {string[]}
     */
    monthHeaders: [],
    /**
     * Dict indexado por nombre de cuenta level_3.
     * Cada entrada es: { [periodoLabel]: number }
     * Ejemplo: { "Productos": { "ene 2025": 1500000, ... }, ... }
     * @type {Object.<string, Object.<string, number>>}
     */
    accountsData: {},
    /** Lista ordenada de cuentas level_3 presentes en los datos */
    accounts: [],
    /** Rango real devuelto por el backend */
    periodRange: { init_date: null, end_date: null },
    isLoading: false,
    error: null,
    /**
     * Resumen para las summary cards: { current_month, ytd, ltm }.
     * Cada entrada incluye period_start, period_end, amount y ratios mom/yoy.
     * Se calcula independiente de los filtros de la tabla porque necesita
     * comparables que pueden quedar fuera del rango visible (LTM, YoY, etc.).
     */
    summaryCards: null,
    isSummaryLoading: false,
    summaryError: null,
};

// ---------------------------------------------------------------------------
// Getters
// ---------------------------------------------------------------------------

const getters = {
    /**
     * Construye las filas de la tabla: una por cuenta level_3
     * más una fila de total al final.
     * @returns {Array<{ label: string, values: number[], isTotal: boolean }>}
     */
    tableRows: (state) => {
        return state.accounts.map((account) => ({
            label: account,
            isTotal: false,
            values: state.monthHeaders.map(
                (header) => state.accountsData[account]?.[header] ?? 0
            ),
        }));
    },

    /**
     * Fila de totales: suma de todas las cuentas por período.
     * @returns {{ label: string, values: number[], isTotal: boolean }}
     */
    totalRow: (state) => {
        const values = state.monthHeaders.map((header) => {
            return state.accounts.reduce((sum, account) => {
                return sum + (state.accountsData[account]?.[header] ?? 0);
            }, 0);
        });
        return { label: "Ventas Netas", values, isTotal: true };
    },
};

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

const mutations = {
    SET_ACTIVE_SUB_FILTER(state, filter) {
        state.activeSubFilter = filter;
    },
    SET_SALES_DATA(state, { accounts, accountsData, monthHeaders, periodRange }) {
        state.accounts = accounts;
        state.accountsData = accountsData;
        state.monthHeaders = monthHeaders;
        state.periodRange = periodRange;
    },
    SET_LOADING(state, isLoading) {
        state.isLoading = isLoading;
    },
    SET_ERROR(state, error) {
        state.error = error;
    },
    SET_SUMMARY_CARDS(state, summary) {
        state.summaryCards = summary;
    },
    SET_SUMMARY_LOADING(state, isLoading) {
        state.isSummaryLoading = isLoading;
    },
    SET_SUMMARY_ERROR(state, error) {
        state.summaryError = error;
    },
};

// ---------------------------------------------------------------------------
// Actions
// ---------------------------------------------------------------------------

const actions = {
    async fetchSalesData({ state, commit, rootState }) {
        commit("SET_LOADING", true);
        commit("SET_ERROR", null);

        try {
            const { initDate, endDate } = resolveDateRange(
                rootState.reportsFilters.endMonth,
                rootState.reportsFilters.selectedPeriodRange
            );

            // Format groupBy parameter (e.g. 'cuentas', 'clientes', or category name)
            const groupBy = state.activeSubFilter.toLowerCase();

            const response = await getSalesByLevel3(initDate, endDate, groupBy);
            const { accounts, report_data: reportData, period_range: periodRange } = response.data;

            // Construir cabeceras de mes dentro del rango real
            const monthHeaders = buildMonthHeaders(periodRange.init_date, periodRange.end_date);

            // Construir dict { account -> { "ene 2025": amount } }
            const accountsData = {};
            accounts.forEach((account) => {
                const row = {};
                (reportData[account] ?? []).forEach((item) => {
                    const label = moment(item.period).format("MMM YYYY").replace(".", "");
                    row[label] = Number(item.total_amount);
                });
                // Freeze por fila: Vue 2 omite el observer recursivo en objetos congelados.
                accountsData[account] = Object.freeze(row);
            });

            commit("SET_SALES_DATA", {
                accounts: Object.freeze(accounts),
                accountsData: Object.freeze(accountsData),
                monthHeaders: Object.freeze(monthHeaders),
                periodRange: Object.freeze(periodRange),
            });
        } catch (error) {
            console.error("[salesReport] Error fetching sales data:", error);
            commit("SET_ERROR", error?.response?.data?.detail ?? "Error al cargar ventas.");
        } finally {
            commit("SET_LOADING", false);
        }
    },

    async fetchSalesSummary({ commit, rootState }) {
        commit("SET_SUMMARY_LOADING", true);
        commit("SET_SUMMARY_ERROR", null);

        try {
            const endDate = moment(rootState.reportsFilters.endMonth)
                .endOf("month")
                .format("YYYY-MM-DD");

            const response = await getSalesSummaryCards(endDate);
            commit("SET_SUMMARY_CARDS", Object.freeze(response.data));
        } catch (error) {
            console.error("[salesReport] Error fetching summary cards:", error);
            commit(
                "SET_SUMMARY_ERROR",
                error?.response?.data?.detail ?? "Error al cargar resumen de ventas."
            );
        } finally {
            commit("SET_SUMMARY_LOADING", false);
        }
    },
};

// ---------------------------------------------------------------------------
// Export
// ---------------------------------------------------------------------------

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions,
};
